import { AppService } from './../../../core/app.service';
import { Component, input, OnInit, OnDestroy, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Input } from "../../forms/input/input";
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ModePassationMarcheService } from '../../../services/modepassation.service';
import { UiButtonComponent } from '../../button/button.component';

@Component({
  selector: 'app-stepfouractivities',
  imports: [ReactiveFormsModule, MatIcon, Input, UiButtonComponent],
  templateUrl: './stepfouractivities.component.html',
  styleUrl: './stepfouractivities.component.css',
  standalone: true,
})
export class StepfouractivitiesComponent implements OnInit, OnDestroy {
  stepfourData = input<any>();
  stepfourChanges = output<any>();
  submitStepFour = output<void>();

  public activity: any = {};
  public modePassationMarches: any[] = [];
  public codePmp: string = "";

  pmpForm: FormGroup = new FormGroup({});
  private subscription: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private modePassationMarcheService: ModePassationMarcheService,
    private AppService: AppService,
  ) {
    this.pmpForm = this.fb.group({
      datePrevision:                   ['', Validators.required],
      delaiNeccessairePassationMarche: [{ value: '', disabled: true }, Validators.required],
      dureePrevisionExecutionMarche:   ['', Validators.required],
      echeanceSatisfaction:            [{ value: '', disabled: true }, Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadModePassationMarche();

    if (this.stepfourData()) {
      this.pmpForm.patchValue(this.stepfourData());
    }

    this.subscription.add(
      this.pmpForm.get('datePrevision')?.valueChanges.subscribe(() => this.calculateEcheanceSatisfaction())
    );
    this.subscription.add(
      this.pmpForm.get('delaiNeccessairePassationMarche')?.valueChanges.subscribe(() => this.calculateEcheanceSatisfaction())
    );
    this.subscription.add(
      this.pmpForm.get('dureePrevisionExecutionMarche')?.valueChanges.subscribe(() => this.calculateEcheanceSatisfaction())
    );

    this.calculateEcheanceSatisfaction();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** Revenir à l'étape précédente (budgets) */
  goBack(): void {
    this.router.navigate([], {
      queryParams: { step: 'budgets' },
      relativeTo: this.route
    });
  }

  onSubmit(): void {
    if (this.pmpForm.valid) {
      // Merger avec les données existantes pour ne pas écraser les étapes précédentes
      const existingData = localStorage.getItem('activity');
      let activityData = existingData ? JSON.parse(existingData) : {};

      activityData = {
        ...activityData,
        stepfour: { ...this.pmpForm.getRawValue(), modePassationMarche: this.codePmp },
      };

      localStorage.setItem('activity', JSON.stringify(activityData));

      // Émettre les données : le parent (add/edit) gère la navigation
      this.stepfourChanges.emit({ ...this.pmpForm.getRawValue(), modePassationMarche: this.codePmp });
    } else {
      this.pmpForm.markAllAsTouched();
    }
  }

  loadModePassationMarche(): void {
    this.modePassationMarcheService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.modePassationMarches = response.data;

        const storedActivity = localStorage.getItem("activity");
        if (storedActivity) {
          try {
            const activityData = JSON.parse(storedActivity);
            const montantEstimatifStr = activityData.budgets?.montantEstimatif;
            const montantEstimatif = parseFloat(montantEstimatifStr || '0');

            if (!isNaN(montantEstimatif) && montantEstimatif > 0) {
              const matchingModeCode = this.trouverCodeParSeuil(this.modePassationMarches, montantEstimatif);
              this.codePmp = matchingModeCode.id as string;
              this.pmpForm.get('delaiNeccessairePassationMarche')?.setValue(matchingModeCode.libelle);
            } else {
              this.pmpForm.get('delaiNeccessairePassationMarche')?.setValue('');
            }
          } catch (error) {
            console.error('Erreur lors du parsing des données depuis localStorage :', error);
            this.pmpForm.get('delaiNeccessairePassationMarche')?.setValue('');
          }
        } else {
          this.pmpForm.get('delaiNeccessairePassationMarche')?.setValue('');
        }
      },
      error: (error) => {
        console.error('Erreur lors du chargement des modes de passation :', error);
      }
    });
  }

  private trouverCodeParSeuil(modes: any[], montantEstimatif: number): any | null {
    let bestMatch: { code: string | null; libelle: string | null; seuil: number } | null = null;

    for (const mode of modes) {
      const seuilCaracteristique = mode.details?.find((detail: any) => detail.code === 'SEUIL');
      if (seuilCaracteristique) {
        const seuilValue = parseFloat(seuilCaracteristique.valeur);
        if (!isNaN(seuilValue) && montantEstimatif <= seuilValue) {
          if (!bestMatch || seuilValue <= bestMatch.seuil) {
            bestMatch = { code: mode.id, libelle: mode.valeur, seuil: seuilValue };
          }
        }
      }
    }

    return bestMatch;
  }

  private calculateEcheanceSatisfaction(): void {
    const datePrevisionStr = this.pmpForm.get('datePrevision')?.value;
    const delaiStr = this.pmpForm.get('delaiNeccessairePassationMarche')?.value;
    const dureeStr = this.pmpForm.get('dureePrevisionExecutionMarche')?.value;

    if (datePrevisionStr && delaiStr !== null && delaiStr !== '' && dureeStr !== null && dureeStr !== '') {
      const datePrevision = new Date(datePrevisionStr);
      const delai = parseInt(delaiStr, 10);
      const duree = parseInt(dureeStr, 10);

      if (!isNaN(datePrevision.getTime()) && !isNaN(delai) && !isNaN(duree)) {
        const calculatedDate = new Date(datePrevision);
        calculatedDate.setDate(calculatedDate.getDate() + delai + duree);
        const formattedDate = calculatedDate.toISOString().split('T')[0];
        this.pmpForm.get('echeanceSatisfaction')?.setValue(formattedDate, { emitEvent: false });
      } else {
        this.pmpForm.get('echeanceSatisfaction')?.setValue('', { emitEvent: false });
      }
    } else {
      this.pmpForm.get('echeanceSatisfaction')?.setValue('', { emitEvent: false });
    }
  }
}